import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';

// Auth Components (kept eager: they gate everything else)
import Login from './components/auth/Login';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/layout/Layout';

// Route components are code-split so the initial bundle stays small
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const ClientList = lazy(() => import('./components/clients/ClientList'));
const ClientForm = lazy(() => import('./components/clients/ClientForm'));
const ClientDetail = lazy(() => import('./components/clients/ClientDetail'));
const ArchivedClients = lazy(() => import('./components/clients/ArchivedClients'));
const PipelineView = lazy(() => import('./components/workflow/PipelineView'));
const OverdueTasks = lazy(() => import('./components/workflow/OverdueTasks'));
const UpcomingStarts = lazy(() => import('./components/workflow/UpcomingStarts'));
const UpcomingTasks = lazy(() => import('./components/workflow/UpcomingTasks'));
const CoverageReport = lazy(() => import('./components/reports/CoverageReport'));
const TrainingCompliance = lazy(() => import('./components/reports/TrainingCompliance'));
const StaffList = lazy(() => import('./components/staff/StaffList'));
const StaffProfile = lazy(() => import('./components/staff/StaffProfile'));
const StaffEdit = lazy(() => import('./components/staff/StaffEdit'));
const NotificationCenter = lazy(() => import('./components/notifications/NotificationCenter'));
const Settings = lazy(() => import('./components/settings/Settings'));
const Profile = lazy(() => import('./components/profile/Profile'));
const UserManagement = lazy(() => import('./components/admin/UserManagement'));
const StaffClientView = lazy(() => import('./components/public/StaffClientView'));

const PageSpinner = () => (
  <div className="flex justify-center items-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

const NotFound = () => (
  <div className="p-6 text-center py-16">
    <p className="text-6xl font-bold text-gray-300 mb-4">404</p>
    <h2 className="text-2xl font-semibold text-gray-900">Page not found</h2>
    <p className="text-gray-600 mt-2 mb-6">
      The page you're looking for doesn't exist or may have moved.
    </p>
    <a
      href="/dashboard"
      className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
    >
      Back to Dashboard
    </a>
  </div>
);

const Unauthorized = () => (
  <div className="p-6 text-center">
    <h2 className="text-2xl font-semibold text-gray-900">Unauthorized Access</h2>
    <p className="text-gray-600 mt-2">You don't have permission to view this page.</p>
  </div>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <Suspense fallback={<PageSpinner />}>
          <Routes>
            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Public: token-authenticated staff client link (no login).
                The bare and wildcard forms matter: SMS clients sometimes
                truncate the token, and without these a DSP lands on the
                login screen instead of a "get a new link" message. */}
            <Route path="/staff-view/:token" element={<StaffClientView />} />
            <Route path="/staff-view" element={<StaffClientView />} />
            <Route path="/staff-view/*" element={<StaffClientView />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />

              {/* Client Routes */}
              <Route path="clients" element={<ClientList />} />
              <Route path="clients/archived" element={<ArchivedClients />} />
              <Route path="clients/new" element={<ClientForm />} />
              <Route path="clients/:id" element={<ClientDetail />} />
              <Route path="clients/:id/edit" element={<ClientForm />} />

              {/* Workflow Routes */}
              <Route path="workflow/pipeline" element={<PipelineView />} />
              <Route path="workflow/overdue" element={<OverdueTasks />} />
              <Route path="workflow/upcoming" element={<UpcomingStarts />} />
              <Route path="workflow/tasks" element={<UpcomingTasks />} />

              {/* Reports Routes */}
              <Route path="reports/coverage" element={<CoverageReport />} />
              <Route path="reports/training" element={<TrainingCompliance />} />

              {/* Staff Routes */}
              <Route path="staff" element={<StaffList />} />
              <Route path="staff/:id" element={<StaffProfile />} />
              {/* The users API only lets Admin/System Admin save staff edits;
                  showing the form to other roles loses their changes */}
              <Route
                path="staff/:id/edit"
                element={
                  <ProtectedRoute requiredRoles={['Admin', 'System Admin']}>
                    <StaffEdit />
                  </ProtectedRoute>
                }
              />

              {/* Notifications */}
              <Route path="notifications" element={<NotificationCenter />} />

              {/* Admin Routes */}
              <Route
                path="admin/users"
                element={
                  <ProtectedRoute requiredRoles={['Admin', 'System Admin']}>
                    <UserManagement />
                  </ProtectedRoute>
                }
              />

              {/* Settings & Profile */}
              <Route path="settings" element={<Settings />} />
              <Route path="profile" element={<Profile />} />

              {/* 404 for unknown authenticated routes */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </AuthProvider>
    </Router>
  );
}

export default App;
